/**
 * The Reports view: every period anyone has spent on this site.
 *
 * One row per period, not per person. Someone who left and came back appears
 * twice, and the explainer above the table says so — the whole reason this view
 * exists is that "who was on site in March" is a different question from "who
 * is on site", and only the audit trail can answer the first.
 */
import { ArrowDown, ArrowUp, Search } from "lucide-react";
import { useMemo } from "react";
import type { CrewPeriod } from "@gb/contracts";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { nextSort, type SortDir, type SortKey } from "./crew-url";

export type StintFilter = "all" | "open" | "closed";

export function filterPeriods(
  periods: readonly CrewPeriod[],
  filters: { stint: StintFilter; role: string; query: string },
): CrewPeriod[] {
  const q = filters.query.trim().toLowerCase();
  return periods.filter((p) => {
    if (filters.stint === "open" && p.endDate !== null) return false;
    if (filters.stint === "closed" && p.endDate === null) return false;
    if (filters.role !== "all" && p.role !== filters.role) return false;
    if (q && !p.name.toLowerCase().includes(q) && !p.personId.toLowerCase().includes(q)) {
      return false;
    }
    return true;
  });
}

/**
 * Order the periods for display.
 *
 * Exported and used by the CSV as well as the table, for the same reason
 * `filterPeriods` is: what is exported is what is on screen, and that has to
 * cover the order as well as which rows are in it. A CSV that disagrees with
 * the view it was taken from is a compliance artefact nobody can reconcile.
 *
 * `localeCompare` on the *label*, not the enum value, so "Crane operator" sorts
 * where a reader expects rather than where `crane-operator` happens to fall.
 * Sorting is stable, and the tie-break is always the row's own identity, so two
 * periods that compare equal never swap places between renders.
 */
export function sortPeriods(
  periods: readonly CrewPeriod[],
  { sort, dir }: { sort: SortKey; dir: SortDir },
): CrewPeriod[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...periods].sort((a, b) => {
    let diff = 0;
    switch (sort) {
      case "name":
        diff = a.name.localeCompare(b.name);
        break;
      case "role":
        diff = a.roleLabel.localeCompare(b.roleLabel);
        break;
      case "started":
        diff = a.startDate.localeCompare(b.startDate);
        break;
      case "offboarded":
        // An open period has no end date. Treat it as later than any real one,
        // so it sorts last ascending and first descending — "still on site"
        // belongs at the recent end of the axis, not wherever an empty string
        // would land. The sign is applied once, on the way out, like every
        // other branch; applying it here too would pin nulls last in both
        // directions.
        if (a.endDate === b.endDate) diff = 0;
        else if (a.endDate === null) diff = 1;
        else if (b.endDate === null) diff = -1;
        else diff = a.endDate.localeCompare(b.endDate);
        break;
    }
    // The tiebreak is on the name, not the id. It used to be on the id and got
    // a stable, roughly alphabetical order for free because ids were name
    // slugs; a uuid sorts by nothing a reader can see, so two rows tied on the
    // chosen column would appear shuffled. `id` stays the final discriminator
    // so the sort is still total when two people share a name.
    if (diff !== 0) return diff * sign;
    const byName = a.name.localeCompare(b.name);
    return byName !== 0 ? byName : a.id.localeCompare(b.id);
  });
}

export function ReportsTab({
  periods,
  stint,
  onStintChange,
  role,
  onRoleChange,
  search,
  onSearchChange,
  onClear,
  sort,
  dir,
  onSortChange,
}: {
  periods: CrewPeriod[];
  stint: StintFilter;
  onStintChange: (next: StintFilter) => void;
  role: string;
  onRoleChange: (next: string) => void;
  search: string;
  onSearchChange: (next: string) => void;
  /** Clears all three in one URL write — three would clobber each other. */
  onClear: () => void;
  sort: SortKey;
  dir: SortDir;
  /** Both keys in one write, for the same reason `onClear` takes all three. */
  onSortChange: (next: { sort: SortKey; dir: SortDir }) => void;
}) {
  const shown = useMemo(
    () => sortPeriods(filterPeriods(periods, { stint, role, query: search }), { sort, dir }),
    [periods, stint, role, search, sort, dir],
  );

  // Only roles that actually appear, so the dropdown never offers a filter that
  // can only return nothing.
  const roles = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of periods) seen.set(p.role, p.roleLabel);
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [periods]);

  const filtered = stint !== "all" || role !== "all" || search.trim() !== "";

  return (
    <>
      <div className="mb-[18px] flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <span className="pointer-events-none absolute top-1/2 left-3.5 flex -translate-y-1/2 text-muted">
            <Search size={17} aria-hidden />
          </span>
          <Input
            withIcon
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search name or crew ID…"
            aria-label="Search name or crew ID"
          />
        </div>

        <Select value={stint} onValueChange={(v) => onStintChange(v as StintFilter)}>
          <SelectTrigger aria-label="Filter by period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stints</SelectItem>
            <SelectItem value="open">Currently on site</SelectItem>
            <SelectItem value="closed">Closed (off-site)</SelectItem>
          </SelectContent>
        </Select>

        <Select value={role} onValueChange={onRoleChange}>
          <SelectTrigger aria-label="Filter by role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {roles.map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filtered && (
          <button
            type="button"
            onClick={onClear}
            className="cursor-pointer text-[0.88rem] font-semibold whitespace-nowrap text-muted hover:text-brand"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="mx-0.5 mt-1.5 mb-1.5 flex items-baseline gap-2.5">
        <div className="font-display text-[1.05rem] font-semibold whitespace-nowrap text-ink">
          On-site periods
        </div>
        <span className="text-[0.92rem] font-semibold whitespace-nowrap text-muted">
          {filtered ? `${shown.length} of ${periods.length}` : periods.length}
        </span>
      </div>
      <p className="mt-0 mb-4 max-w-[740px] text-[0.86rem] leading-[1.55] text-muted">
        Audit trail of every period a crew member was on site. Each row is one
        period — if someone was offboarded and later re-onboarded, they appear
        twice.
      </p>

      {/* Real `<table>` markup rather than the flex-row `DataTable`: sorting's
          whole accessible expression is `aria-sort` on a column header, and
          there is no header to put it on in a grid of divs. `ui/table.tsx` was
          written for this table — its docstring names it, down to the 780px
          min-width — and had never been wired up. */}
      <Table className="min-w-[780px]">
        <TableHeader>
          <TableRow interactive={false}>
            <TableHead className="w-[230px]">Crew ID</TableHead>
            <SortableHead
              column="name"
              label="Name"
              sort={sort}
              dir={dir}
              onSort={onSortChange}
            />
            <SortableHead
              column="role"
              label="Role"
              className="w-[140px]"
              sort={sort}
              dir={dir}
              onSort={onSortChange}
            />
            <SortableHead
              column="started"
              label="Started"
              className="w-[120px]"
              sort={sort}
              dir={dir}
              onSort={onSortChange}
            />
            <SortableHead
              column="offboarded"
              label="Offboarded"
              className="w-[130px]"
              sort={sort}
              dir={dir}
              onSort={onSortChange}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.length === 0 ? (
            <TableEmpty colSpan={5}>No on-site periods match.</TableEmpty>
          ) : (
            shown.map((p) => (
              <TableRow key={p.id} interactive={false}>
                <TableCell className="font-mono text-[0.84rem]">
                  {p.stintTotal > 1
                    ? `${p.personId} · stint ${p.stintNo}/${p.stintTotal}`
                    : p.personId}
                </TableCell>
                {/* No avatar chip. It carried no information the name beside
                    it did not already give, and in a table it read as a column
                    of its own that could not be sorted or explained. */}
                <TableCell className="text-[0.95rem] font-bold">{p.name}</TableCell>
                <TableCell>{p.roleLabel}</TableCell>
                <TableCell className="font-mono text-[0.86rem]">{p.startDate}</TableCell>
                <TableCell className="font-mono text-[0.86rem]">
                  {p.endDate === null ? (
                    <span className="text-ok">— on site</span>
                  ) : (
                    p.endDate
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </>
  );
}

/**
 * A column header that sorts.
 *
 * `aria-sort` goes on the `<th>` and the click target is a button inside it,
 * which is the pairing screen readers actually announce — a clickable `<th>`
 * with no button is not reachable by keyboard, and a button carrying
 * `aria-sort` is not a column header. The arrow only appears on the active
 * column: an indicator on every header tells you nothing about which one is in
 * effect.
 */
function SortableHead({
  column,
  label,
  className,
  sort,
  dir,
  onSort,
}: {
  column: SortKey;
  label: string;
  className?: string;
  sort: SortKey;
  dir: SortDir;
  onSort: (next: { sort: SortKey; dir: SortDir }) => void;
}) {
  const active = sort === column;
  const Arrow = dir === "asc" ? ArrowUp : ArrowDown;

  return (
    <TableHead
      className={className}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(nextSort({ sort, dir }, column))}
        className={cn(
          "-mx-1 flex items-center gap-1 rounded-control px-1 py-0.5",
          "font-display text-[0.68rem] font-semibold uppercase tracking-[0.08em]",
          "transition-colors hover:text-ink",
          active ? "text-brand" : "text-muted",
        )}
      >
        {label}
        {active ? (
          <Arrow size={13} aria-hidden />
        ) : (
          // Holds the arrow's width so the label does not shift when a column
          // becomes the active one.
          <span aria-hidden className="inline-block w-[13px]" />
        )}
      </button>
    </TableHead>
  );
}
